/**
 * Service pour l'autocomplétion d'adresses françaises
 * Utilise l'API Adresse du gouvernement français (gratuite et open source)
 * Documentation : https://adresse.data.gouv.fr/api-doc/adresse
 */

export interface AdresseFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  properties: {
    label: string; // Adresse complète (ex: "8 Boulevard du Port 80000 Amiens")
    score: number; // Score de pertinence (0 à 1)
    housenumber?: string; // Numéro de voie
    id: string; // Identifiant unique
    type: 'housenumber' | 'street' | 'locality' | 'municipality';
    name: string; // Nom de la voie
    postcode: string; // Code postal
    citycode: string; // Code INSEE
    city: string; // Nom de la commune
    context: string; // Contexte (département, région)
    importance: number; // Importance
    street?: string; // Nom de la rue (si type = housenumber)
  };
}

export interface AdresseResponse {
  type: 'FeatureCollection';
  version: string;
  features: AdresseFeature[];
  attribution: string;
  licence: string;
  query: string;
  limit: number;
}

/**
 * Recherche d'adresses via l'API Adresse du gouvernement français
 * @param query Texte de recherche (ex: "8 bd du port")
 * @param limit Nombre de résultats max (défaut: 5)
 * @returns Liste d'adresses correspondantes
 */
export const searchAdresse = async (
  query: string,
  limit: number = 5
): Promise<AdresseFeature[]> => {
  // Ne pas faire de requête si la recherche est trop courte
  if (!query || query.trim().length < 3) {
    return [];
  }

  try {
    const params = new URLSearchParams({
      q: query.trim(),
      limit: limit.toString(),
      autocomplete: '1', // Active le mode autocomplétion
    });

    const response = await fetch(
      `https://api-adresse.data.gouv.fr/search/?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Erreur API Adresse: ${response.status}`);
    }

    const data: AdresseResponse = await response.json();
    return data.features || [];
  } catch (error) {
    console.error('Erreur lors de la recherche d\'adresse:', error);
    return [];
  }
};

/**
 * Formatte une adresse pour l'affichage dans le formulaire
 * Extrait l'adresse, le code postal et la ville
 */
export const formatAdresseForForm = (feature: AdresseFeature): {
  adresse: string;
  codePostal: string;
  ville: string;
} => {
  const { properties } = feature;

  // Construire l'adresse (numéro + rue)
  let adresse = '';
  if (properties.housenumber) {
    adresse = `${properties.housenumber} ${properties.street || properties.name}`;
  } else {
    adresse = properties.name;
  }

  return {
    adresse: adresse.trim(),
    codePostal: properties.postcode,
    ville: properties.city,
  };
};

const adresseService = {
  searchAdresse,
  formatAdresseForForm,
};

export default adresseService;
